import React, { useState, useEffect } from "react";
import axios from 'axios';
import {
    Card,
    CardBody,
    Col,
    Row,
    Container,
    Alert,
} from "reactstrap";

// Import Breadcrumb
import Breadcrumbs from "../../components/Common/Breadcrumb";

const FormAddStudyMaterial = () => {
    document.title = "Study Material | Uma Classes";

    const [formData, setFormData] = useState({
        title: "",
        categoryId: "",
        subcategoryId: "",
        subSubcategoryId: "",
        file: null // Field for file upload
    });

    const [categories, setCategories] = useState([]);
    const [subcategories, setSubcategories] = useState([]);
    const [subSubcategories, setSubSubcategories] = useState([]);
    const [successMessage, setSuccessMessage] = useState("");
    const [errorMessage, setErrorMessage] = useState("");
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const token = localStorage.getItem('token');

        const fetchCategories = async () => {
            try {
                const response = await axios.get('https://uma.aasmo.in/api/admin/getall-category', {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "multipart/form-data",
                    }
                });
                setCategories(response); // Assuming the API returns an array of categories
                setLoading(false);
            } catch (error) {
                console.error("Error fetching categories:", error);
                setLoading(false);
            }
        };

        const fetchSubCategories = async () => {
            try {
                const response = await axios.get(`https://uma.aasmo.in/api/admin/getall-subcategory`, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    }
                });
                setSubcategories(response); // Assuming the API returns an array of subcategories
            } catch (error) {
                console.error("Error fetching subcategories:", error);
            }
        };

        const fetchSubSubCategories = async () => {
            try {
                const response = await axios.get(`https://uma.aasmo.in/api/admin/getall-subsubcategory`, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    }
                });
                setSubSubcategories(response); // Assuming the API returns an array of sub-subcategories
            } catch (error) {
                console.error("Error fetching sub-subcategories:", error);
            }
        };

        fetchCategories();
        fetchSubCategories();
        fetchSubSubCategories();
    }, []);

    const handleCategoryChange = (e) => {
        const { value } = e.target;
        setFormData({
            ...formData,
            categoryId: value,
            subcategoryId: "",
            subSubcategoryId: ""
        });
    };

    const handleSubcategoryChange = (e) => {
        const { value } = e.target;
        setFormData({
            ...formData,
            subcategoryId: value,
            subSubcategoryId: ""
        });
    };

    const handleChange = (e) => {
        const { name, value, files } = e.target;
        setFormData({
            ...formData,
            [name]: files ? files[0] : value, // Handle file upload
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const token = localStorage.getItem('token');
            const formDataToSubmit = new FormData(); // Create a FormData object

            formDataToSubmit.append('title', formData.title);
            formDataToSubmit.append('file', formData.file);
            formDataToSubmit.append('category', formData.categoryId);
            formDataToSubmit.append('subcategory', formData.subcategoryId);
            formDataToSubmit.append('subSubcategory', formData.subSubcategoryId);
            await axios.post('https://uma.aasmo.in/api/admin/add-study-material', formDataToSubmit, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                }
            });

            setSuccessMessage("File added successfully");
            setErrorMessage("");
            setFormData({ title: "", file: null }); // Reset form
        } catch (error) {
            console.error("Error adding file:", error);
            setSuccessMessage("");
            setErrorMessage(error.response?.data?.message || "Error adding file"); // Access the error message properly
        }
    };

    if (loading) {
        return <div>Loading categories...</div>; // Optional loading message
    }

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid={true}>
                    <Breadcrumbs title="Study Material" breadcrumbItem="Add" />

                    <Row>
                        <Col>
                            <Card>
                                <CardBody>
                                    <form onSubmit={handleSubmit}>
                                        <Row className="mb-3">
                                            <label htmlFor="file" className="col-md-2 col-form-label">
                                                File (JPEG, PNG, PDF)
                                            </label>
                                            <div className="col-md-12">
                                                <input
                                                    className="form-control"
                                                    type="file"
                                                    id="file"
                                                    name="file"
                                                    accept=".jpeg,.jpg,.png,.pdf"
                                                    onChange={handleChange}
                                                    required
                                                />
                                            </div>
                                        </Row>

                                        <Row className="mb-3">
                                            <label htmlFor="title" className="col-form-label">
                                                Title
                                            </label>
                                            <div className="col-md-12">
                                                <input
                                                    className="form-control"
                                                    type="text"
                                                    id="title"
                                                    name="title"
                                                    value={formData.title}
                                                    onChange={handleChange}
                                                    required
                                                />
                                            </div>
                                        </Row>

                                        <Row className="mb-3">
                                            <label htmlFor="categoryId" className="col-md-2 col-form-label">
                                                Select Category
                                            </label>
                                            <div className="col-md-12">
                                                <select
                                                    id="categoryId"
                                                    name="categoryId"
                                                    className="form-control"
                                                    value={formData.categoryId}
                                                    onChange={handleCategoryChange}
                                                    required
                                                >
                                                    <option value="">Select a category</option>
                                                    {categories.map(category => (
                                                        <option key={category._id} value={category._id}>
                                                            {category.name}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        </Row>

                                        <Row className="mb-3">
                                            <label htmlFor="subcategoryId" className="col-md-2 col-form-label">
                                                Select Subcategory
                                            </label>
                                            <div className="col-md-12">
                                                <select
                                                    id="subcategoryId"
                                                    name="subcategoryId"
                                                    className="form-control"
                                                    value={formData.subcategoryId}
                                                    onChange={handleSubcategoryChange}
                                                    required
                                                >
                                                    <option value="">Select a subcategory</option>
                                                    {subcategories.map(subcategory => (
                                                        <option key={subcategory._id} value={subcategory._id}>
                                                            {subcategory.name}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        </Row>

                                        <Row className="mb-3">
                                            <label htmlFor="subSubcategoryId" className="col-md-4 col-form-label">
                                                Select Sub-Subcategory
                                            </label>
                                            <div className="col-md-12">
                                                <select
                                                    id="subSubcategoryId"
                                                    name="subSubcategoryId"
                                                    className="form-control"
                                                    value={formData.subSubcategoryId}
                                                    onChange={handleChange}
                                                    required
                                                >
                                                    <option value="">Select a sub-subcategory</option>
                                                    {subSubcategories.map(subSubcategory => (
                                                        <option key={subSubcategory._id} value={subSubcategory._id}>
                                                            {subSubcategory.name}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        </Row>

                                        {successMessage && <Alert color="success">{successMessage}</Alert>}
                                        {errorMessage && <Alert color="danger">{errorMessage}</Alert>}

                                        <button type="submit" className="btn btn-primary">Submit</button>
                                    </form>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </div>
        </React.Fragment>
    );
};

export default FormAddStudyMaterial;
